import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { ArrowLeft, FileText, DownloadSimple, Printer, Leaf, CalendarBlank, Thermometer, Wind, Drop, Flask, CheckCircle } from '@phosphor-icons/react';
import { TurfPulseLogoSVG } from '../components/TurfPulseLogo';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../config/api';

export const SeasonReportPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation(['common', 'nav', 'dashboard']);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const printRef = useRef();

  useEffect(() => {
    fetchReportData();
  }, []);

  const fetchReportData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/reports/season-summary`, { withCredentials: true });
      setReportData(response.data);
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Failed to load season report');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExportPDF = async () => {
    try {
      toast.info('Generating PDF...');
      const response = await axios.get(`${API_URL}/api/reports/season-pdf`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `TurfPulse_Season_Report_${reportData?.season_year || new Date().getFullYear()}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded!');
    } catch (error) {
      console.error('PDF generation failed:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const handleExportJSON = () => {
    if (!reportData) return;
    const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `turfpulse_season_report_${reportData.season_year}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success('Report exported');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading report data...</div>
      </div>
    );
  }

  if (!reportData) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <FileText weight="duotone" size={48} className="mx-auto mb-4 text-muted-foreground" />
            <p className="text-lg font-medium">No report data available</p>
            <p className="text-sm text-muted-foreground mt-2">Start logging applications to generate your season report.</p>
            <Button className="mt-4" onClick={() => navigate('/app')}>Go to Dashboard</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { statistics, applications_by_type, epa_compliance, applications, soil_tests } = reportData;

  return (
    <div className="min-h-screen bg-background">
      {/* Header - Hidden when printing */}
      <header className="bg-background/70 backdrop-blur-xl border-b border-border shadow-sm sticky top-0 z-50 print:hidden">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <TurfPulseLogoSVG animated={false} size={36} />
            <h1 className="text-xl font-bold">Season Report</h1>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handlePrint}>
              <Printer weight="bold" size={18} className="mr-2" />
              Print
            </Button>
            <Button variant="default" onClick={handleExportPDF} data-testid="export-pdf-btn">
              <FileText weight="bold" size={18} className="mr-2" />
              Download PDF
            </Button>
            <Button variant="outline" onClick={handleExportJSON}>
              <DownloadSimple weight="bold" size={18} className="mr-2" />
              Export JSON
            </Button>
            <Button variant="outline" onClick={() => navigate('/app')}>
              <ArrowLeft weight="bold" size={18} className="mr-2" />
              Dashboard
            </Button>
          </div>
        </div>
      </header>

      {/* Report Content */}
      <div ref={printRef} className="max-w-5xl mx-auto p-6 print:p-0 print:max-w-none">
        {/* Report Header */}
        <div className="text-center mb-8 print:mb-4">
          <div className="flex items-center justify-center gap-3 mb-4 print:mb-2">
            <TurfPulseLogoSVG animated={false} size={64} />
            <span className="text-4xl font-bold text-[#0D9256] print:text-3xl">TurfPulse</span>
          </div>
          <h1 className="text-2xl font-medium text-muted-foreground print:text-xl">Season Report</h1>
          <p className="text-lg text-muted-foreground">{reportData.season_year} Growing Season</p>
          <p className="text-sm text-muted-foreground mt-2">
            Generated: {new Date(reportData.report_date).toLocaleDateString()}
          </p>
        </div>

        {/* User & Property Info */}
        <Card className="mb-6 print:shadow-none print:border-gray-300">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Property Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Owner</p>
                <p className="font-medium">{reportData.user?.name || 'TurfPulse User'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Grass Type</p>
                <p className="font-medium capitalize">{reportData.user?.grass_type || 'Not specified'}</p>
              </div>
              {reportData.property && (
                <>
                  <div>
                    <p className="text-sm text-muted-foreground">Property Name</p>
                    <p className="font-medium">{reportData.property.name || 'My Property'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Location</p>
                    <p className="font-medium">
                      {[reportData.property.city, reportData.property.state].filter(Boolean).join(', ') || 'Not specified'}
                    </p>
                  </div>
                </>
              )}
            </div>
            {reportData.zones?.length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm text-muted-foreground mb-2">Zones ({reportData.zones.length})</p>
                <div className="flex flex-wrap gap-2">
                  {reportData.zones.map((zone, idx) => (
                    <span key={idx} className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                      {zone.name} {zone.square_footage ? `(${zone.square_footage.toLocaleString()} sq ft)` : ''}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Season Statistics */}
        <Card className="mb-6 print:shadow-none print:border-gray-300">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Season Statistics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-primary/5 rounded-lg">
                <Leaf weight="duotone" size={24} className="mx-auto mb-2 text-primary" />
                <p className="text-2xl font-bold">{statistics.total_applications}</p>
                <p className="text-xs text-muted-foreground">Total Applications</p>
              </div>
              <div className="text-center p-4 bg-green-500/10 rounded-lg">
                <Flask weight="duotone" size={24} className="mx-auto mb-2 text-green-600" />
                <p className="text-2xl font-bold">{statistics.products_used}</p>
                <p className="text-xs text-muted-foreground">Products Used</p>
              </div>
              <div className="text-center p-4 bg-amber-500/10 rounded-lg">
                <Thermometer weight="duotone" size={24} className="mx-auto mb-2 text-amber-600" />
                <p className="text-2xl font-bold">{statistics.average_gdd_at_application}</p>
                <p className="text-xs text-muted-foreground">Avg GDD at Application</p>
              </div>
              <div className="text-center p-4 bg-blue-500/10 rounded-lg">
                <CalendarBlank weight="duotone" size={24} className="mx-auto mb-2 text-blue-600" />
                <p className="text-2xl font-bold">{statistics.soil_tests_count}</p>
                <p className="text-xs text-muted-foreground">Soil Tests</p>
              </div>
            </div>

            {/* Applications by Type */}
            {Object.keys(applications_by_type).length > 0 && (
              <div className="mt-6">
                <p className="text-sm font-medium mb-3">Applications by Type</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {Object.entries(applications_by_type).map(([type, count]) => (
                    <div key={type} className="flex justify-between items-center p-2 bg-secondary/50 rounded">
                      <span className="text-sm">{type}</span>
                      <span className="font-bold">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Products List */}
            {statistics.products_list?.length > 0 && (
              <div className="mt-6">
                <p className="text-sm font-medium mb-2">Products Applied</p>
                <div className="flex flex-wrap gap-2">
                  {statistics.products_list.map((product, idx) => (
                    <span key={idx} className="text-xs bg-muted px-2 py-1 rounded">
                      {product}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* EPA Compliance Summary */}
        <Card className="mb-6 print:shadow-none print:border-gray-300 border-green-200 dark:border-green-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <CheckCircle weight="duotone" size={20} className="text-green-600" />
              EPA Compliance Summary
            </CardTitle>
            <CardDescription>Weather conditions recorded at time of application</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4 text-center">
              <div className="p-4 bg-green-500/10 rounded-lg">
                <p className="text-2xl font-bold text-green-600">{epa_compliance.records_with_weather}</p>
                <p className="text-xs text-muted-foreground">Records with Weather Data</p>
              </div>
              <div className="p-4 bg-secondary/50 rounded-lg">
                <p className="text-2xl font-bold">{epa_compliance.total_records}</p>
                <p className="text-xs text-muted-foreground">Total Application Records</p>
              </div>
              <div className="p-4 bg-primary/10 rounded-lg">
                <p className="text-2xl font-bold">{epa_compliance.compliance_rate}%</p>
                <p className="text-xs text-muted-foreground">Compliance Rate</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-4 text-center">
              Weather data (temperature, humidity, wind speed, wind direction) is auto-captured at application time for EPA drift mitigation record-keeping.
            </p>
          </CardContent>
        </Card>

        {/* Recent Applications Detail */}
        {applications?.length > 0 && (
          <Card className="mb-6 print:shadow-none print:border-gray-300">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Application Log (Last {Math.min(applications.length, 20)})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-2">Date</th>
                      <th className="text-left py-2 px-2">Product</th>
                      <th className="text-left py-2 px-2">Type</th>
                      <th className="text-center py-2 px-2">GDD</th>
                      <th className="text-center py-2 px-2">Temp</th>
                      <th className="text-center py-2 px-2">Wind</th>
                      <th className="text-center py-2 px-2">Humidity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {applications.slice(0, 20).map((app, idx) => (
                      <tr key={idx} className="border-b hover:bg-muted/30">
                        <td className="py-2 px-2">{new Date(app.applied_at).toLocaleDateString()}</td>
                        <td className="py-2 px-2 font-medium">{app.product_name}</td>
                        <td className="py-2 px-2">
                          <span className="text-xs bg-secondary px-2 py-0.5 rounded">{app.product_type}</span>
                        </td>
                        <td className="py-2 px-2 text-center">{Math.round(app.gdd_at_application)}</td>
                        <td className="py-2 px-2 text-center">
                          {app.temperature_f ? `${Math.round(app.temperature_f)}°F` : '-'}
                        </td>
                        <td className="py-2 px-2 text-center">
                          {app.wind_speed_mph ? (
                            <span className="flex items-center justify-center gap-1">
                              <Wind size={14} />
                              {Math.round(app.wind_speed_mph)} {app.wind_direction || ''}
                            </span>
                          ) : '-'}
                        </td>
                        <td className="py-2 px-2 text-center">
                          {app.humidity_percent ? (
                            <span className="flex items-center justify-center gap-1">
                              <Drop size={14} />
                              {Math.round(app.humidity_percent)}%
                            </span>
                          ) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Soil Tests */}
        {soil_tests?.length > 0 && (
          <Card className="mb-6 print:shadow-none print:border-gray-300">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Soil Test History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {soil_tests.slice(0, 5).map((test, idx) => (
                  <div key={idx} className="p-3 bg-muted/50 rounded-lg">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">{test.lab_name || 'Soil Test'}</p>
                        <p className="text-xs text-muted-foreground">
                          {test.test_date ? new Date(test.test_date).toLocaleDateString() : 'Date unknown'}
                        </p>
                      </div>
                      <div className="text-right">
                        {test.ph && <span className="text-sm">pH: {test.ph}</span>}
                      </div>
                    </div>
                    {/* Handle recommendations - can be string or array of objects */}
                    {test.recommendations && typeof test.recommendations === 'string' && (
                      <p className="text-sm text-muted-foreground mt-2">{test.recommendations}</p>
                    )}
                    {test.recommendations && Array.isArray(test.recommendations) && (
                      <div className="mt-2 space-y-1">
                        {test.recommendations.slice(0, 3).map((rec, ridx) => (
                          <p key={ridx} className="text-sm text-muted-foreground">
                            • {typeof rec === 'string' ? rec : rec.issue || rec.recommendation || 'Recommendation'}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center text-sm text-muted-foreground mt-8 print:mt-4">
          <p>Generated by TurfPulse - Smart Lawn Care Powered by GDD</p>
          <p className="text-xs mt-1">getturfpulse.com</p>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .print\\:hidden { display: none !important; }
          .print\\:shadow-none { box-shadow: none !important; }
          .print\\:border-gray-300 { border-color: #d1d5db !important; }
          .print\\:p-0 { padding: 0 !important; }
          .print\\:max-w-none { max-width: none !important; }
          .print\\:mb-4 { margin-bottom: 1rem !important; }
          .print\\:mb-2 { margin-bottom: 0.5rem !important; }
          .print\\:text-2xl { font-size: 1.5rem !important; }
          .print\\:mt-4 { margin-top: 1rem !important; }
        }
      `}</style>
    </div>
  );
};

export default SeasonReportPage;
